import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { API, WS_BASE } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Power, Globe, LogOut, Wifi, Send } from "lucide-react";

const TOKEN_KEY = "rt_admin_token";

function Login({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao entrar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex items-center justify-center px-4"
      style={{ minHeight: "100vh", background: "#001b3d" }}
    >
      <Card className="w-full max-w-sm p-8 bg-[#002d62] border-white/10 text-white">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck className="h-6 w-6 text-sky-400" />
          <h1 className="text-lg font-semibold tracking-tight">Painel Administrativo</h1>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-white/70">Usuário</Label>
            <Input
              data-testid="admin-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="bg-[#001b3d] border-white/15 text-white"
              autoComplete="username"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70">Senha</Label>
            <Input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#001b3d] border-white/15 text-white"
              autoComplete="current-password"
            />
          </div>
          <Button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="w-full bg-sky-500 hover:bg-sky-400 text-white"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}
          </Button>
        </form>
      </Card>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [settings, setSettings] = useState(null);
  const [clients, setClients] = useState([]);
  const [linkDraft, setLinkDraft] = useState("");
  const [connected, setConnected] = useState(false);
  const [now, setNow] = useState(Date.now());
  const wsRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const elapsed = (iso) => {
    if (!iso) return "0s";
    const secs = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 1000));
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const connectWs = useCallback(() => {
    const ws = new WebSocket(`${WS_BASE}/ws/admin?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onclose = () => {
      setConnected(false);
      if (!localStorage.getItem(TOKEN_KEY)) return;
      setTimeout(() => connectWs(), 2000);
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "state") {
          setSettings(msg.settings);
          setClients(msg.clients || []);
          setLinkDraft((prev) => (prev === "" ? msg.settings.redirect_link : prev));
        }
      } catch (_) {}
    };
  }, [token]);

  useEffect(() => {
    connectWs();
    return () => {
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [connectWs]);

  const update = async (patch) => {
    try {
      const { data } = await axios.put(`${API}/admin/settings`, patch, auth);
      setSettings(data.settings);
      toast.success("Configuração atualizada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const saveLink = () => {
    if (!linkDraft.trim()) return toast.error("Informe um link válido");
    update({ redirect_link: linkDraft.trim() });
  };

  const release = async (id) => {
    try {
      await axios.post(`${API}/admin/release`, { client_id: id }, auth);
      toast.success("Cliente liberado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao liberar");
    }
  };

  if (!settings) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: "100vh", background: "#001b3d" }}>
        <Loader2 className="h-6 w-6 animate-spin text-sky-400" />
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#001b3d" }} className="text-white">
      <header className="border-b border-white/10 bg-[#002d62]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-sky-400" />
            <span className="font-semibold tracking-tight">Painel de Liberação</span>
            <span
              data-testid="ws-status"
              className={`ml-3 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                connected ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"
              }`}
            >
              <Wifi className="h-3 w-3" /> {connected ? "Ao vivo" : "Reconectando"}
            </span>
          </div>
          <Button
            data-testid="admin-logout-button"
            variant="ghost"
            onClick={onLogout}
            className="text-white/70 hover:text-white hover:bg-white/5"
          >
            <LogOut className="h-4 w-4 mr-1" /> Sair
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 grid gap-6 lg:grid-cols-3">
        {/* Settings column */}
        <div className="space-y-6 lg:col-span-1">
          <Card className="p-5 bg-[#002d62] border-white/10 text-white">
            <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Send className="h-4 w-4 text-sky-400" /> Link de redirecionamento
            </h2>
            <Input
              data-testid="redirect-link-input"
              value={linkDraft}
              onChange={(e) => setLinkDraft(e.target.value)}
              placeholder="https://..."
              className="bg-[#001b3d] border-white/15 text-white mb-3"
            />
            <Button
              data-testid="save-link-button"
              onClick={saveLink}
              className="w-full bg-sky-500 hover:bg-sky-400 text-white"
            >
              Salvar link
            </Button>
          </Card>

          <Card className="p-5 bg-[#002d62] border-white/10 text-white space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-start gap-2">
                <Globe className="h-4 w-4 text-sky-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Somente IP do Brasil</p>
                  <p className="text-xs text-white/50">Bloqueia acessos de fora do Brasil</p>
                </div>
              </div>
              <Switch
                data-testid="br-only-switch"
                checked={settings.br_only}
                onCheckedChange={(v) => update({ br_only: v })}
              />
            </div>

            <div className="flex items-center justify-between gap-3 pt-3 border-t border-white/10">
              <div className="flex items-start gap-2">
                <Power className={`h-4 w-4 mt-0.5 ${settings.system_enabled ? "text-emerald-400" : "text-red-400"}`} />
                <div>
                  <p className="text-sm font-medium">Sistema ligado</p>
                  <p className="text-xs text-white/50">Desligar faz o site retornar erro 500</p>
                </div>
              </div>
              <Switch
                data-testid="system-enabled-switch"
                checked={settings.system_enabled}
                onCheckedChange={(v) => update({ system_enabled: v })}
              />
            </div>
          </Card>
        </div>

        {/* Clients column */}
        <div className="lg:col-span-2">
          <Card className="p-5 bg-[#002d62] border-white/10 text-white">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold">Clientes online aguardando</h2>
              <span
                data-testid="clients-count"
                className="text-xs bg-sky-500/15 text-sky-300 px-2 py-0.5 rounded-full"
              >
                {clients.length} online
              </span>
            </div>

            {clients.length === 0 ? (
              <p data-testid="no-clients-message" className="text-sm text-white/40 py-10 text-center">
                Nenhum cliente aguardando no momento.
              </p>
            ) : (
              <div className="space-y-2" data-testid="clients-list">
                {clients.map((c) => (
                  <div
                    key={c.id}
                    data-testid={`client-row-${c.id}`}
                    className="flex items-center justify-between gap-4 rounded-lg bg-[#001b3d] border border-white/10 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="font-mono text-sm truncate" data-testid={`client-ip-${c.id}`}>
                        {c.ip || "IP desconhecido"}
                      </p>
                      <p className="text-xs text-white/50 truncate">
                        {c.country || "—"} {c.country_code ? `(${c.country_code})` : ""}
                      </p>
                      <p
                        className="text-xs text-sky-300/80 mt-0.5"
                        data-testid={`client-wait-${c.id}`}
                      >
                        Aguardando há {elapsed(c.connected_at)}
                      </p>
                    </div>
                    <Button
                      data-testid={`release-button-${c.id}`}
                      onClick={() => release(c.id)}
                      className="bg-emerald-500 hover:bg-emerald-400 text-white shrink-0"
                      size="sm"
                    >
                      Liberar
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <Login onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
